import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import type { MessengerExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { MenuModal as MessengerMenuModal } from '../components/menuModal';

type Option = {
  id: string;
  label: string;
  nextNodeId: string;
};

export type MessengerMenuPayload = {
  options: Option[];
  text: string;
};

type MessengerMenuNodeAttributes = GenericNodeAttributes<
  MessengerMenuPayload,
  MessengerExtraData
>;
type MessengerMenuNodeProps = GenericNodeProps<
  MessengerMenuPayload,
  MessengerExtraData
>;

const translatePayload: MessengerMenuNodeAttributes['translatePayload'] = (
  node,
) => {
  const {
    payload: { options },
  } = node;

  return options.map(({ id, label, nextNodeId }) => ({
    id: id,
    label,
    connection: nextNodeId || uuid(),
  }));
};

const updateConnection: MessengerMenuNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;
  const { options } = payload;
  const newOptions = options.concat();

  const labelIndex = options.findIndex(
    (opt) => opt.label === connection.connectionLabel,
  );

  if (labelIndex !== -1) {
    newOptions[labelIndex] = {
      ...options[labelIndex],
      nextNodeId: connectionId,
    };
  }

  return {
    ...node,
    payload: {
      ...payload,
      options: newOptions,
    },
  };
};

export const MessengerMenuNodeAttributes: MessengerMenuNodeAttributes = {
  iconName: 'channels',
  label: 'Menu',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: MessengerMenuNode,
  ActionModal: MessengerMenuModal,
  helpText: 'Allow users to choose a path and simple ask clickable questions',
};

export function MessengerMenuNode({
  node,
}: MessengerMenuNodeProps): JSX.Element {
  const { payload } = node;
  const { options } = payload;

  const optionCount = Object.keys(options).length;
  const optionText = optionCount === 1 ? 'Option' : 'Options';

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="semibold" text={`${optionCount} ${optionText}`} />
          </TextEllipsis>
        ),
        [optionCount, optionText],
      )}
    </TextContent>
  );
}
